import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { openDialog } from "redux/features/dialog/dialogSlice"
import { selectSettingsById } from "redux/features/settings/settingsSlice"
import { formatDate, formatMoney } from "helpers/formatters"
import {
  isIssuesCreate,
  isPaymentRequestsCreate,
  isPaymentSchedulesCreate,
  isPaymentSchedulesShow,
  isFreshFinance,
} from "helpers/features"
import { PAYMENT_PAY_MODAL, SEPA_PAYMENT_PAY_MODAL } from "config"
import Doughnut from "components/ui/Doughnut"
import Title from "components/Debt/Title"
import Manager from "components/Debt/Manager"
import Summary from "components/Debt/Summary"
import Person from "components/Debt/Person"
import Information from "components/ui/InfoList"
import Card from "components/ui/CardAccordion"
import {
  CollapsedAside,
  Balance,
  CollapsedActions,
  CollapsedLightButton,
  CollapsedPrimaryButton,
  Container,
  Aside,
  Content,
  Info,
  ActionList,
  ActionListItem,
  LightButton,
  PrimaryButton,
} from "components/Debt/elements"
import { DebtType, SettingsType } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  item: DebtType
}

const DebtCourtPreparing = ({ item }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const settings = useAppSelector(
    (state) => selectSettingsById(state, 0) as SettingsType,
  )

  const {
    id,
    start_date,
    balance,
    unpaid_total_sum,
    court_application,
    payment_schedule,
    creditor,
    manager,
    company,
  } = item

  const general = [
    {
      label: t("features.debt.list.start_date.title"),
      value: formatDate(start_date),
      visible: true,
    },
    {
      label: t("features.debt.list.creditor.title"),
      value: creditor?.name,
      visible: true,
    },
    {
      label: t("features.debt.list.company.title"),
      value: company?.legal_name,
      visible: isFreshFinance(),
    },
  ].filter((item) => item.value && item.visible)

  const courtItems = [
    {
      label: t("features.debt.list.court.case"),
      value: court_application?.matter_number,
    },
    {
      label: t("features.debt.list.court.start_date"),
      value: formatDate(court_application?.start_date),
    },
    {
      label: t("features.debt.list.court.decision_date"),
      value: formatDate(court_application?.decision_date),
    },
    {
      label: t("features.debt.list.court.diary"),
      value: court_application?.diary_number,
    },
  ]

  const handlePaymentCreate = (
    event: React.SyntheticEvent,
    amount: number | undefined,
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    dispatch(
      openDialog({
        type: isPaymentRequestsCreate(settings?.features)
          ? PAYMENT_PAY_MODAL
          : SEPA_PAYMENT_PAY_MODAL,
        content: {
          debtId: id,
          amount: amount,
        },
      }),
    )
  }

  const handlePaymentScheduleCreate = () => {
    navigate(`/debts/${id}/payment_schedules/new`)
  }

  const handlePaymentScheduleView = () => {
    navigate(`/debts/${id}/payment_schedules/${payment_schedule?.id}`)
  }

  const handleIssueCreate = () => {
    navigate(`/debts/${id}/issues/new`)
  }

  return (
    <Card
      collapsed={
        <>
          <CollapsedAside>
            <Title compact debt={item} />
            <Balance variant="h4">{formatMoney(balance)}</Balance>
          </CollapsedAside>
          <CollapsedActions>
            {isPaymentRequestsCreate(settings?.features) && (
              <CollapsedLightButton
                onClick={(event) =>
                  handlePaymentCreate(event, unpaid_total_sum)
                }
              >
                {t("features.debt.actions.payment.overdue.title", {
                  amount: formatMoney(unpaid_total_sum),
                })}
              </CollapsedLightButton>
            )}
            {isIssuesCreate(settings?.features) && (
              <CollapsedLightButton onClick={handleIssueCreate}>
                {t("features.debt.actions.issue.create.short_title")}
              </CollapsedLightButton>
            )}
            {isPaymentRequestsCreate(settings?.features) && (
              <CollapsedPrimaryButton
                onClick={(event) => handlePaymentCreate(event, balance)}
              >
                {t("features.debt.actions.payment.fully.title")}
              </CollapsedPrimaryButton>
            )}
          </CollapsedActions>
        </>
      }
      expanded={
        <Container>
          <Aside>
            <Doughnut
              color="error"
              top={t("features.debt.doughnut.remain.title")}
              amount={formatMoney(balance)}
              value={100}
            />
            <Summary item={item} />
          </Aside>
          <Content>
            <Info>
              <Title debt={item} />
              <Manager dense manager={manager} />
              <Information dense items={general} />
              <Information
                sx={{ margin: "21px 0 0 0" }}
                dense
                items={courtItems}
              />
              {court_application && court_application.court && (
                <Person
                  title={t("features.debt.list.court.title")}
                  person={court_application.court}
                />
              )}
            </Info>
            <ActionList>
              {isPaymentRequestsCreate(settings?.features) && (
                <ActionListItem>
                  <PrimaryButton
                    onClick={(event) =>
                      handlePaymentCreate(event, unpaid_total_sum)
                    }
                  >
                    {t("features.debt.actions.payment.overdue.title", {
                      amount: formatMoney(unpaid_total_sum),
                    })}
                  </PrimaryButton>
                </ActionListItem>
              )}
              {isPaymentRequestsCreate(settings?.features) && (
                <ActionListItem>
                  <PrimaryButton
                    onClick={(event) => handlePaymentCreate(event, balance)}
                  >
                    {t("features.debt.actions.payment.fully.title")}
                  </PrimaryButton>
                </ActionListItem>
              )}
              <ActionListItem>
                {payment_schedule ? (
                  <>
                    {isPaymentSchedulesShow(settings?.features) && (
                      <LightButton onClick={handlePaymentScheduleView}>
                        {t("features.debt.actions.payment_schedule.open.title")}
                      </LightButton>
                    )}
                  </>
                ) : (
                  <>
                    {isPaymentSchedulesCreate(settings?.features) && (
                      <LightButton onClick={handlePaymentScheduleCreate}>
                        {t(
                          "features.debt.actions.payment_schedule.create.title",
                        )}
                      </LightButton>
                    )}
                  </>
                )}
              </ActionListItem>
              {isIssuesCreate(settings?.features) && (
                <ActionListItem>
                  <LightButton onClick={handleIssueCreate}>
                    {t("features.debt.actions.issue.create.title")}
                  </LightButton>
                </ActionListItem>
              )}
            </ActionList>
          </Content>
        </Container>
      }
    />
  )
}

export default DebtCourtPreparing
